import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { askPropertyChat, type PropertyChatReply } from '../lib/viewings-api.js';
import { V4Group } from './ux-v4.js';

/**
 * Krok AI8 — chatbot nabídky na veřejné stránce prohlídky (`/prohlidka/<token>`).
 *
 * Stránka NIC nerozhoduje: co je odpověď z faktů, co je předání makléři,
 * jak zní představení jako AI i text o souhlasu — to všechno posílá server
 * (`POST /v1/public/viewings/:token/chat`, `@reos/ai` `property-chat.ts`).
 * UI je jen okno konverzace, aby nebyly dvě pravdy o tom, co chatbot smí.
 *
 * Kontakt se posílá JEN se zaškrtnutým souhlasem — a i kdyby ho UI poslalo
 * bez něj, server to odmítne (422), takže souhlas není jen ozdoba formuláře.
 *
 * ROZHODNUTÍ NAP6-A16 (jedna cesta k rezervaci): chat SÁM rezervaci
 * NENABÍZÍ, dokud je výběr termínu na téže stránce — jen na něj ukáže
 * (`viewingPickerId`). Dvě cesty k témuž (tlačítko v chatu i mřížka termínů
 * vedle) by znamenaly dva formuláře s jiným chováním při kolizi termínu.
 * Hotová služba `bookChatViewing` (`@reos/crm`, AI8) proto ZŮSTÁVÁ bez
 * volajícího z UI — je to rozhraní pro povrch, kde výběr termínu vedle
 * NENÍ (např. chat vložený na cizí web); podmínku jejího zapojení drží
 * tenhle komentář a `docs/architecture` změnu nepotřebuje.
 */

interface Turn {
  readonly role: 'visitor' | 'assistant';
  readonly text: string;
}

export function PropertyChat({
  token,
  viewingPickerId,
}: {
  readonly token: string;
  /** Id bloku s výběrem termínu na téže stránce; `undefined` = na stránce není. */
  readonly viewingPickerId?: string | undefined;
}): React.JSX.Element {
  const t = useT();
  const [turns, setTurns] = React.useState<readonly Turn[]>([]);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [reply, setReply] = React.useState<PropertyChatReply | null>(null);
  const [handoff, setHandoff] = React.useState(false);
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [thanks, setThanks] = React.useState(false);

  async function send(contact: { name?: string; email?: string; phone?: string } | null): Promise<void> {
    const question = draft.trim();
    if (contact === null && question === '') return;
    setBusy(true);
    setFailed(false);
    try {
      const answer = await askPropertyChat(token, {
        message: contact === null ? question : (question === '' ? t('webAdmin.publicViewing.chatContactSend') : question),
        history: turns.map((turn) => ({ role: turn.role, text: turn.text })),
        ...(contact === null ? {} : { contact, consent: true }),
      });
      setReply(answer);
      setTurns((current) => [
        ...current,
        { role: 'visitor', text: question === '' ? t('webAdmin.publicViewing.chatContactSend') : question },
        { role: 'assistant', text: answer.text },
      ]);
      setDraft('');
      // „Nevím" = makléř to má u sebe; nabídneme kontakt, aby lead nezmizel.
      if (answer.kind === 'handoff') setHandoff(true);
      if (contact !== null) {
        setThanks(true);
        setHandoff(false);
      }
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  // Představení jako AI musí stát U VSTUPU, ne až u odpovědi (AI Act čl. 50),
  // proto tentýž klíč katalogu, jaký posílá server (`ai.chat.disclaimer`);
  // po první odpovědi se použije text serveru — jedna pravda, dvě cesty k ní.
  const disclaimer = reply?.disclaimer ?? t('ai.chat.disclaimer');
  const consentNotice = reply?.consentNotice ?? t('ai.chat.consentNotice');

  return (
    <V4Group testId="property-chat" label={t('webAdmin.publicViewing.chatHeading')}>
      <h2 className="rh-v4-h2">{t('webAdmin.publicViewing.chatHeading')}</h2>
      <p className="rh-a2-status" data-testid="property-chat-disclaimer">
        {disclaimer}
      </p>
      {viewingPickerId === undefined ? null : (
        <p className="rh-a2-status" data-testid="property-chat-booking-pointer">
          <a href={`#${viewingPickerId}`} className="rh-a2-link">
            {t('webAdmin.publicViewing.chatBookingPointer')}
          </a>
        </p>
      )}
      {turns.length > 0 ? (
        <ol className="rh-public-viewing__chat" data-testid="property-chat-turns">
          {turns.map((turn, index) => (
            <li key={`${turn.role}-${index}`} data-testid={`property-chat-turn-${turn.role}`}>
              <strong>
                {turn.role === 'visitor' ? t('webAdmin.publicViewing.chatYou') : t('webAdmin.publicViewing.chatAssistant')}
                {': '}
              </strong>
              {turn.text}
            </li>
          ))}
        </ol>
      ) : null}
      {failed ? (
        <p role="alert" className="rh-a2-error" data-testid="property-chat-failed">
          {t('webAdmin.publicViewing.chatFailed')}
        </p>
      ) : null}
      {thanks ? (
        <p role="status" className="rh-a2-status" data-testid="property-chat-thanks">
          {t('webAdmin.publicViewing.chatThanks')}
        </p>
      ) : null}
      <label className="rh-v4-field">
        <span className="rh-v4-field__label">{t('webAdmin.publicViewing.chatHeading')}</span>
        <input
          value={draft}
          placeholder={t('webAdmin.publicViewing.chatPlaceholder')}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="property-chat-input"
        />
      </label>
      <div className="rh-public-viewing__actions">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--tinted"
          disabled={busy || draft.trim() === ''}
          onClick={() => void send(null)}
          data-testid="property-chat-send"
        >
          {busy ? t('webAdmin.publicViewing.chatSending') : t('webAdmin.publicViewing.chatSend')}
        </button>
      </div>
      {handoff ? (
        <div className="rh-public-viewing__form" data-testid="property-chat-contact">
          <h3 className="rh-v4-h2">{t('webAdmin.publicViewing.chatContactHeading')}</h3>
          <label className="rh-v4-field">
            <span className="rh-v4-field__label">{t('webAdmin.publicViewing.name')}</span>
            <input value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} data-testid="property-chat-name" />
          </label>
          <label className="rh-v4-field">
            <span className="rh-v4-field__label">{t('webAdmin.publicViewing.email')}</span>
            <input
              type="email"
              value={email}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              data-testid="property-chat-email"
            />
          </label>
          <label className="rh-v4-field">
            <span className="rh-v4-field__label">{t('webAdmin.publicViewing.phone')}</span>
            <input
              type="tel"
              value={phone}
              autoComplete="tel"
              onChange={(e) => setPhone(e.target.value)}
              data-testid="property-chat-phone"
            />
          </label>
          <label className="rh-v4-field rh-v4-field--check">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="property-chat-consent"
            />
            <span className="rh-v4-field__label">{t('webAdmin.publicViewing.chatConsent')}</span>
          </label>
          <p className="rh-v4-head__lede" data-testid="property-chat-consent-notice">
            {consentNotice}
          </p>
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled"
            disabled={busy || !consent || (email.trim() === '' && phone.trim() === '')}
            onClick={() =>
              void send({
                ...(name.trim() === '' ? {} : { name: name.trim() }),
                ...(email.trim() === '' ? {} : { email: email.trim() }),
                ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
              })
            }
            data-testid="property-chat-contact-send"
          >
            {t('webAdmin.publicViewing.chatContactSend')}
          </button>
        </div>
      ) : null}
    </V4Group>
  );
}
